import React, { useContext, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion/dist/framer-motion'
import parse, { domToReact } from 'html-react-parser'

import { DataContext } from '../../Context/DataContext'
import { UIContext } from '../../Context/UIContext'

import useLockBodyScroll from '../../useLockBodyScroll'
import usePreloadImage from '../../usePreloadImage'

import './Modal.scss'

const options = {
    replace: ({ attribs, children }) => {
        if (!attribs) return;

        const tagNames = ['h1', 'h2', 'h3', 'p', 'ul'];
        const tagName = attribs.tagName;

        if (tagNames.includes(tagName)) {
            if (tagName === 'ul') {
                return (
                    <ul>
                        {children.map((child, i) => 
                            <li key={`sub-item-${i}`}>
                                {domToReact(child.children, options)}
                            </li>
                        )}
                    </ul>
                );
            } else {
                return React.createElement(tagName, {}, domToReact(children, options));
            }
        }
    }
}

const Modal = React.forwardRef(({ showTitle, className }, ref) => {
    const { strings } = useContext(DataContext)
    const { selectedModal, setModalVisible } = useContext(UIContext)
    const imageLoaded = usePreloadImage(selectedModal?.image)
    const classes = className ? `mga-modal ${className}` : 'mga-modal'

    useLockBodyScroll(selectedModal !== null)

    const handleExit = useCallback(() => {
        setModalVisible(false)
    }, [setModalVisible])

    return (
        <AnimatePresence mode="wait">
            { selectedModal &&
                <motion.div
                    ref={ref}
                    key="mga-modal"
                    className={classes}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.4 }}}
                    transition={{ duration: 0.75, type: 'tween' }}
                >
                    <div className="mga-modal__overlay" onClick={handleExit} />
                    <motion.div className="mga-modal__container">
                        <button
                            className="mga-modal__exit"
                            onClick={handleExit}
                        >
                            { strings.close }
                        </button>
                        <div className="mga-modal__wrapper">
                            { selectedModal.url ?
                                <a
                                    className={`mga-modal__logo${!imageLoaded ? ' mga-img-loading' : ''}`}
                                    href={selectedModal.url}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                >
                                    <img src={selectedModal.image} alt={selectedModal.title} />
                                </a>
                            : 
                                <div
                                    className={`mga-modal__logo${!imageLoaded ? ' mga-img-loading' : ''}`}
                                >
                                    <img src={selectedModal.image} alt={selectedModal.title} />
                                </div>
                            }
                            { showTitle && <h2 className="mga-modal__title">{ selectedModal.title }</h2> }
                            <div className="mga-modal__info">
                                { parse(selectedModal.info, options) }
                            </div>
                            { selectedModal.url &&
                                <a className="mga-modal__link" href={selectedModal.url} target="_blank" rel="nofollow noopener noreferrer">{ strings.visit }</a>
                            }
                        </div>
                    </motion.div>
                </motion.div>
            }
        </AnimatePresence>
    )
})

Modal.defaultProps = {
    showTitle: false
}

const MotionModal = motion(Modal)

export default MotionModal